import { KeyboardEvent, useEffect, useId, useState } from "react";
import { ImSearch } from "react-icons/im";
import { useTranslation } from "react-i18next";

export type SearchInputV2Props = {
  className?: string;
  id?: string;
  name: string;
  type?: string;
  value?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  suggestions?: string[];
  onChange?: (value: string) => void;
  onSearch: (value: string | undefined) => void;
};

export function SearchInputV2({
  className,
  id,
  name,
  type,
  value,
  label,
  placeholder,
  disabled = false,
  suggestions,
  onChange,
  onSearch,
}: SearchInputV2Props) {
  const { t } = useTranslation();
  const generatedId = useId().replace(/:/g, "");
  const inputId = id || `ov2-search-${name.replace(/[^a-zA-Z0-9_-]/g, "-")}-${generatedId}`;
  const datalistId = suggestions && suggestions.length > 0 ? `${inputId}-datalist` : undefined;

  const [text, setText] = useState<string>(value ?? "");

  useEffect(() => {
    setText("");
  }, [type]);

  useEffect(() => {
    if (value !== undefined) setText(value);
  }, [value]);

  const computedPlaceholder =
    placeholder ?? `${t("commonUi.search").toUpperCase()}${type ? ` ${type.toUpperCase()}` : ""}...`;

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      onSearch(text);
    }
  }

  function handleChange(nextValue: string) {
    setText(nextValue);
    if (onChange) onChange(nextValue);
  }

  return (
    <label className={`ov2-field ov2-search-field ${className || ""}`} htmlFor={inputId}>
      {label && <span className="ov2-field__label">{label}</span>}
      <span
        className={`ov2-input ov2-search ${disabled ? "ov2-input--disabled" : ""}`}
      >
        <input
          id={inputId}
          name={name}
          type="text"
          value={text}
          placeholder={computedPlaceholder}
          disabled={disabled}
          autoComplete="off"
          list={datalistId}
          maxLength={150}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="ov2-search__icon"
          disabled={disabled}
          onClick={() => onSearch(text)}
          aria-label={t("commonUi.search")}
        >
          <ImSearch />
        </button>
      </span>
      {datalistId && suggestions && (
        <datalist id={datalistId}>
          {suggestions.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
      )}
    </label>
  );
}
